/**
 * PropertiesEmptyState
 * 
 * Shown on the Properties List page when there are no Msool units yet.
 * Prompts the user to go to channels management to connect channels and import units.
 * Includes a decorative faded mock table underneath the CTA.
 */

import { FC, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { Box, Typography, Button } from '@mui/material';
import { T_KEYS } from '../../constants';
import { ChannelLogo } from '../ChannelLogos';

const Container = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '51px',
  padding: '50px 24px',
  overflow: 'hidden',
  borderRadius: '24px',
  backgroundColor: '#FFFFFF',
  border: '1px solid #E2E8F0',
  position: 'relative',
});

const TopSection = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '24px',
  zIndex: 1,
});

const Title = styled(Typography)({
  fontSize: '24px',
  fontWeight: 700,
  color: '#331860',
  fontFamily: '"Tajawal", sans-serif',
  textAlign: 'center',
});

const Description = styled(Typography)({
  fontSize: '14px',
  fontWeight: 400,
  color: '#625783',
  fontFamily: '"Tajawal", sans-serif',
  textAlign: 'center',
  maxWidth: '541px',
});

const CTAButton = styled(Button)({
  borderRadius: '8px',
  backgroundColor: '#5B3FFF',
  color: '#FFFFFF',
  fontSize: '14px',
  fontWeight: 700,
  fontFamily: '"Tajawal", sans-serif',
  textTransform: 'none',
  padding: '16px 97px',
  whiteSpace: 'nowrap',
  '&:hover': {
    backgroundColor: '#4A2FE0',
  },
});

// Mock table styles
const MockTableContainer = styled(Box)({
  position: 'relative',
  width: '100%',
  maxWidth: '756px',
  opacity: 0.8,
});

const MockTable = styled(Box)({
  display: 'flex',
  width: '100%',
});

const MockColumn = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
});

const MockHeader = styled(Box)({
  padding: '5px 8px',
  borderBottom: '1px solid #E9E9E7',
  fontFamily: '"Tajawal", sans-serif',
  fontSize: '10px',
  fontWeight: 500,
  color: '#37352F',
  textAlign: 'end',
});

const MockCell = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: '8px',
  padding: '6px 8px',
  borderBottom: '1px solid #E9E9E7',
  height: '32px',
});

const MockImage = styled(Box)({
  width: '43px',
  height: '20px',
  borderRadius: '2px',
  backgroundColor: '#E5E5E5',
  flexShrink: 0,
});

const MockText = styled(Box)<{ width?: string }>(({ width = '80px' }) => ({
  height: '8px',
  borderRadius: '4px',
  backgroundColor: '#E5E5E5',
  width,
}));

const MockDots = styled(Box)({
  width: '16px',
  height: '4px',
  display: 'flex',
  gap: '2px',
  alignItems: 'center',
  justifyContent: 'center',
  '& span': {
    width: '3px',
    height: '3px',
    borderRadius: '50%',
    backgroundColor: '#C4C4C4',
    display: 'block',
  },
});


const GradientOverlay = styled(Box)({
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  height: '200px',
  background: 'linear-gradient(to bottom, rgba(255,255,255,0) 0%, rgba(255,255,255,1) 100%)',
  pointerEvents: 'none',
});

const MOCK_ROWS = 6;

const PropertiesEmptyState: FC = memo(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleNavigateToChannels = () => {
    navigate('/channels');
  };

  return (
    <Container>
      <TopSection>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px' }}>
          <Title>{t(T_KEYS.PROPERTIES_EMPTY_TITLE)}</Title>
          <Description>{t(T_KEYS.PROPERTIES_EMPTY_DESC)}</Description>
        </Box>
        <CTAButton variant="contained" onClick={handleNavigateToChannels}>
          {t(T_KEYS.PROPERTIES_EMPTY_CTA)}
        </CTAButton>
      </TopSection>

      <MockTableContainer>
        <MockTable>
          {/* Actions column (start) */}
          <MockColumn sx={{ flex: '0 0 60px' }}>
            <MockHeader>&nbsp;</MockHeader>
            {Array.from({ length: MOCK_ROWS }).map((_, i) => (
              <MockCell key={`action-${i}`} sx={{ justifyContent: 'center' }}>
                <MockDots>
                  <span /><span /><span />
                </MockDots>
              </MockCell>
            ))}
          </MockColumn>

          {/* Channels column */}
          <MockColumn sx={{ flex: '0 0 120px' }}>
            <MockHeader>{t(T_KEYS.CHANNELS)}</MockHeader>
            {Array.from({ length: MOCK_ROWS }).map((_, i) => (
              <MockCell key={`channels-${i}`} sx={{ justifyContent: 'center', gap: '4px' }}>
                <ChannelLogo channel="airbnb" size={18} rounded />
                <ChannelLogo channel="booking" size={18} rounded />
                <ChannelLogo channel="gathern" size={18} rounded />
              </MockCell>
            ))}
          </MockColumn>

          {/* Address column */}
          <MockColumn>
            <MockHeader>{t(T_KEYS.ADDRESS)}</MockHeader>
            {Array.from({ length: MOCK_ROWS }).map((_, i) => (
              <MockCell key={`address-${i}`} sx={{ justifyContent: 'flex-end' }}>
                <MockText width={`${120 + (i % 3) * 20}px`} />
              </MockCell>
            ))}
          </MockColumn>

          {/* Property column */}
          <MockColumn>
            <MockHeader>{t(T_KEYS.PROPERTY)}</MockHeader>
            {Array.from({ length: MOCK_ROWS }).map((_, i) => (
              <MockCell key={`property-${i}`} sx={{ justifyContent: 'flex-end' }}>
                <MockText width="100px" />
                <MockImage />
              </MockCell>
            ))}
          </MockColumn>
        </MockTable>
        <GradientOverlay />
      </MockTableContainer>
    </Container>
  );
});

PropertiesEmptyState.displayName = 'PropertiesEmptyState';

export default PropertiesEmptyState;
